import React, { useState } from "react";
import { Text, Badge, Button, Skeleton, Flex } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarDays, ChevronRight, CircleAlert,
  Lightbulb, Mail, MapPin, MessageSquare, RefreshCw, Star,
  Store, Users, Wifi,
} from "lucide-react";
import { useProfileContext } from "context";
import { DatePickerWithRange } from "components";
import HomeIntegrations from "./HomeIntegrations";
import HomeBusinessProfilePosts from "./HomeBusinessProfilePosts";
import HomeActivityPulse from "./HomeActivityPulse";
import useHomeDashboardData, { getHomePeriod } from "./useHomeDashboardData";
import "./HomeDashboard.css";

const SERVICES = [
  {
    id: "wifi", title: "Guest Wi-Fi", subtitle: "Every connection is an opportunity.",
    icon: Wifi, tone: "violet", to: "/guest-wifi/connections", action: "Manage Wi-Fi",
    footer: "Turn a warm welcome into a lasting connection.",
  },
  {
    id: "email", title: "Email marketing", subtitle: "Give your guests a reason to return.",
    icon: Mail, tone: "teal", to: "/marketing/analytics", actionTo: "/marketing/campaigns",
    action: "Manage campaigns", footer: "The next great visit starts in their inbox.",
  },
  {
    id: "reviews", title: "Reviews", subtitle: "Good conversations build your reputation.",
    icon: Star, tone: "amber", to: "/reviews/overview", actionTo: "/reviews/reviews",
    action: "Respond to reviews", footer: "A thoughtful reply makes all the difference.",
  },
];

const numberFormatter = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 });

function formatMetric(metric) {
  if (metric.value === null || metric.value === undefined) return "—";
  if (metric.format === "percent") return `${numberFormatter.format(metric.value)}%`;
  if (metric.format === "rating") return Number(metric.value).toFixed(1);
  return numberFormatter.format(metric.value);
}

function ServiceCard({ service, data }) {
  const Icon = service.icon;
  return (
    <article className={`home-service-card home-tone-${service.tone}`} aria-labelledby={`home-${service.id}-title`} aria-busy={data.isLoading}>
      <div className="home-service-heading">
        <span className="home-service-icon"><Icon size={21} strokeWidth={1.7} /></span>
        <Link className="home-text-link" to={service.to} aria-label={`View ${service.title}`}>
          View <ArrowRight size={15} />
        </Link>
      </div>
      <h2 id={`home-${service.id}-title`}>{service.title}</h2>
      <p className="home-service-subtitle">{service.subtitle}</p>

      <div className="home-metrics">
        {data.metrics.map((metric) => {
          return (
            <div key={metric.id} className={`home-metric ${metric.wide ? "home-metric-wide" : ""}`}>
              <span className="home-metric-label">{metric.label}</span>
              <Skeleton loading={data.isLoading}>
                <span className={`home-metric-value ${metric.tone === "orange" ? "home-metric-attention" : ""}`}>
                  {formatMetric(metric)}
                  {metric.format === "rating" && metric.value != null && <Star size={18} fill="currentColor" strokeWidth={0} aria-label="out of 5 stars" />}
                </span>
              </Skeleton>
              <span className="home-metric-detail">{metric.detail || "\u00a0"}</span>
            </div>
          );
        })}
      </div>

      <div className="home-service-footer">
        {data.isError ? (
          <div className="home-service-state" role="status">
            <CircleAlert size={15} />
            <span>Some data couldn’t be loaded.</span>
            <button type="button" onClick={() => data.refetch()} className="home-retry" aria-label={`Retry ${service.title}`}><RefreshCw size={13} /> Retry</button>
          </div>
        ) : (
          <p className="home-service-note">
            {data.isEmpty ? data.emptyMessage : service.footer}
          </p>
        )}
        <Button asChild variant="outline" color="gray" size="3" className="home-service-button">
          <Link to={service.actionTo || service.to}>
            {service.action}
            <ArrowRight size={16} />
          </Link>
        </Button>
      </div>
    </article>
  );
}

const recommendationIcons = { reviews: MessageSquare, "email marketing": Mail, "guest wi-fi": Wifi, "business profile": Store };

function Recommendation({ item }) {
  const Icon = recommendationIcons[item.category?.toLowerCase()] || Lightbulb;
  return (
    <Link className="home-recommendation" to={item.to}>
      <span className={`home-recommendation-icon ${item.tone === "orange" ? "home-recommendation-warning" : ""}`}><Icon size={19} strokeWidth={1.7} /></span>
      <div className="home-recommendation-copy">
        <div className="home-recommendation-title">
          <h3>{item.title}</h3>
          {item.tone === "orange" && <Badge color="orange" size="1" variant="soft">Needs attention</Badge>}
        </div>
        <p>{item.description}</p>
        <span className="home-recommendation-action">{item.actionLabel} <ArrowRight size={13} /></span>
      </div>
      <ChevronRight className="home-recommendation-chevron" size={17} />
    </Link>
  );
}

export default function HomeDashboard() {
  const { userProfile } = useProfileContext();
  const [selectedDateRange, setSelectedDateRange] = useState(() => getHomePeriod("last7"));
  const data = useHomeDashboardData(selectedDateRange);
  const firstName = userProfile?.user_first_name?.trim();
  const today = new Date();
  const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div className="home-dashboard">
      <main className="home-scroll-area">
        <Flex direction="column" p="5" width="100%" maxWidth="1920px" mx="auto">
          <section className="home-welcome" aria-labelledby="home-title">
            <Flex direction="column" gap="1">
              <Text size="1" color="blue" weight="bold">YOUR BUSINESS, CONNECTED.</Text>
              <Text size="7" weight="bold">Welcome back{firstName ? `, ${firstName}` : ""}</Text>
              <Text size="2" color="gray" weight="medium">Let's make every guest connection count. Here's how things are looking.</Text>
            </Flex>
            <div className="home-date">
              <CalendarDays size={16} /><time dateTime={localDate}>{today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</time>
            </div>
          </section>

          <section aria-labelledby="home-overview-title">
            <div className="home-section-heading home-overview-heading">
              <div className="home-overview-label"><h2 id="home-overview-title">Your overview</h2><span className="home-scope"><MapPin size={13} /> All available locations</span></div>
              <div className="home-period-filter">
                <DatePickerWithRange
                  className="home-date-range"
                  value={selectedDateRange}
                  onDateChange={(range) => {
                    if (range?.from && range?.to) setSelectedDateRange(range);
                  }}
                  showEndDateOnOpen
                />
              </div>
            </div>
            <div className="home-service-grid">
              {SERVICES.map((service) => <ServiceCard key={service.id} service={service} data={data[service.id]} />)}
            </div>
            <p className="home-scope-note">Wi-Fi and reviews cover your available locations. Email marketing covers your organisation.</p>
          </section>

          <HomeActivityPulse activity={data.activity} periodLabel={data.periodLabel} />

          <HomeBusinessProfilePosts />

          <HomeIntegrations />

          <div className="home-lower-grid home-lower-grid-single">
            <section aria-labelledby="home-recommendations-title">
              <div className="home-section-heading">
                <div>
                  <h2 id="home-recommendations-title"><Lightbulb size={19} strokeWidth={1.7} /> Recommended for you</h2>
                  <p>Small steps to get more from Bconnect.</p>
                </div>
                <span className="home-recommendation-count">{data.recommendations.length} suggestions</span>
              </div>
              <div className="home-recommendations">
                {data.recommendations.map((item) => <Recommendation key={item.id} item={item} />)}
              </div>
            </section>

            {/* AI assistant panel is intentionally hidden until the feature is ready.
            <aside className="home-assistant-panel" aria-labelledby="home-assistant-title">
              <div className="home-assistant-topline"><span className="home-assistant-symbol"><Sparkles size={23} strokeWidth={1.5} /></span><span>YOUR AI ASSISTANT</span></div>
              <h2 id="home-assistant-title">A little inspiration.<br />Coming soon.</h2>
              <p>We’re shaping a new assistant to help with campaign ideas, guest communication and your next best action.</p>
              <div className="home-assistant-coming-soon"><Sparkles size={16} /><span>AI assistance</span><strong>Coming soon</strong></div>
              <span className="home-assistant-footnote">We’ll let you know when it’s ready.</span>
            </aside>
            */}
          </div>

          <section className="home-quick-actions" aria-label="Quick links">
            <span className="home-quick-label">Pick up where it matters</span>
            <Link to="/reviews/business-profile"><Store size={16} /> Business profile <ArrowRight size={14} /></Link>
            <Link to="/marketing/audience"><Users size={16} /> Your audience <ArrowRight size={14} /></Link>
            <Link to="/guest-wifi/captive-portal"><Wifi size={16} /> Guest portal <ArrowRight size={14} /></Link>
          </section>
        </Flex>
      </main>
    </div>
  );
}
